/**
 * LeadLoop queue partitioning.
 *
 * Pure so the filter semantics and the default-tab rule can be tested without
 * mounting the view.
 *
 * The buckets are attention, not pipeline. LeadLoop's job is the stretch between
 * "somebody contacted the business" and "this is a real, sufficiently qualified
 * contact", and inside that stretch there are only three questions worth a tab:
 *
 *   attention  what needs me right now?
 *   scheduled  what is still in play but not asking for anything?
 *   all        everything the loop is holding
 *
 * The middle bucket is named for the follow-up date that put it there, not
 * "Active" — Active is a lifecycle status, and most of the leads under Needs
 * Attention are Active too, so a tab with that name would claim to hold records it
 * does not.
 *
 * Intake is not a tab in this Lead-only partition. Calls and Form Entries have a
 * dedicated Actionable Intake section above it, while these filters ask only about
 * the timing of Leads that have already passed through intake.
 *
 * Finished work — verified & qualified, dismissed — is deliberately absent. It
 * never enters the feed at all (the server drops terminal leads), so there is no
 * tab for it here; the Leads page filter is where history is read.
 */

import type { LoopItem } from './leadloop-api';

export const LOOP_FILTERS = ['attention', 'scheduled', 'all'] as const;
export type LoopFilter = (typeof LOOP_FILTERS)[number];

/**
 * The reasons that mean "not now": a lead with a follow-up date still in the
 * future. Everything else in the feed wants a person.
 *
 * This is the whole snooze mechanism, and it is derived from the date rather than
 * stored as a status — so a lead comes back on its own when the date arrives,
 * with nobody having to remember to change anything.
 */
const RESTING_REASONS = new Set(['scheduled']);

export function matchesLoopFilter(item: LoopItem, filter: LoopFilter): boolean {
  switch (filter) {
    case 'attention':
      return !RESTING_REASONS.has(item.reason);
    case 'scheduled':
      return RESTING_REASONS.has(item.reason);
    default:
      return true;
  }
}

export function loopFilterCounts(items: LoopItem[]): Record<LoopFilter, number> {
  return {
    attention: items.filter((item) => matchesLoopFilter(item, 'attention')).length,
    scheduled: items.filter((item) => matchesLoopFilter(item, 'scheduled')).length,
    all: items.length,
  };
}

/**
 * Which tab a visit opens on. Always the work.
 *
 * The intake desk has its own section, so the default inside this Lead-only queue
 * is always Needs Attention.
 */
export const DEFAULT_LOOP_FILTER: LoopFilter = 'attention';

export const LOOP_FILTER_EMPTY: Record<LoopFilter, string> = {
  attention: 'Nothing needs you right now. Leads come back here when their follow-up is due.',
  scheduled: 'No leads are waiting on a scheduled follow-up.',
  all: 'Nothing needs contact right now — the loop is clear.',
};

export const LOOP_FILTER_LABELS: [LoopFilter, string][] = [
  ['attention', 'Needs Attention'],
  ['scheduled', 'Scheduled'],
  ['all', 'Everything'],
];
